import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SectionHeading } from "@/components/SectionHeading";
import { useContent } from "@/lib/useContent";
import { useJsonLd } from "@/lib/seo";

export const FAQSection = () => {
  const faqs = useContent("faqs");
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const cats = useMemo(() => ["All", ...Array.from(new Set(faqs.map((f) => f.cat)))], [faqs]);
  const faqSchema = useMemo(() => faqs.length === 0 ? null : ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  }), [faqs]);
  useJsonLd("ld-faq", faqSchema);
  const items = faqs.filter((f) =>
    (cat === "All" || f.cat === cat) &&
    (!q || f.q.toLowerCase().includes(q.toLowerCase()) || f.a.toLowerCase().includes(q.toLowerCase()))
  );
  return (
    <section className="py-24 lg:py-32 bg-white" data-testid="faq-section" id="faq">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="FAQ" title="Frequently asked questions" subtitle="Visas, flights, hotels, packages, payments and privacy — answered by our consultants." />
        <div className="relative mb-6 max-w-md mx-auto">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions…"
            className="w-full rounded-full border border-[#E5E7EB] bg-[#F9FAFB] pl-11 pr-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A62A]"
            data-testid="faq-search-input" />
        </div>
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`faq-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${cat === c ? "bg-[#0A2E4D] text-white" : "bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB]"}`}>{c}</button>
          ))}
        </div>
        {items.length === 0 && <p className="text-center text-sm text-[#4B5563]" data-testid="faq-no-results">No matching questions — try different keywords or WhatsApp us directly.</p>}
        <Accordion type="single" collapsible className="space-y-3">
          {items.map((f, i) => (
            <AccordionItem key={f.q} value={`item-${i}`} className="border border-[#E5E7EB] rounded-xl px-6 data-[state=open]:border-[#D4A62A]">
              <AccordionTrigger className="text-left text-[#0A2E4D] font-semibold hover:no-underline text-sm sm:text-base" data-testid={`faq-item-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-[#4B5563] leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
